import { mediaVariantId, type MediaVariantId } from "./MediaVariant";
import { templateContentsId, type TemplateContentsId } from "./TemplateContents";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for media_usage */
export type MediaUsageId = string & { __flavor: "MediaUsageId" };

/**
 * Represents the table public.media_usage
 * @uniqueConstraints {"media_usage_pkey":{"name":"media_usage_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"media_usage_unique":{"name":"media_usage_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"variant_id","definition":"variant_id","predicate":null},{"name":"template_content_id","definition":"template_content_id","predicate":null}]},"idx_media_usage_variant":{"name":"idx_media_usage_variant","isPrimary":false,"isUnique":false,"columns":[{"name":"variant_id","definition":"variant_id","predicate":null}]},"idx_media_usage_template_content":{"name":"idx_media_usage_template_content","isPrimary":false,"isUnique":false,"columns":[{"name":"template_content_id","definition":"template_content_id","predicate":null}]},"idx_media_usage_created_at":{"name":"idx_media_usage_created_at","isPrimary":false,"isUnique":false,"columns":[{"name":"created_at","definition":"created_at","predicate":null}]}}
 */
export default interface MediaUsageTable {
  id: ColumnType<MediaUsageId, MediaUsageId | undefined, MediaUsageId>;

  variantId: ColumnType<MediaVariantId, MediaVariantId | undefined, MediaVariantId>;

  templateContentId: ColumnType<
    TemplateContentsId,
    TemplateContentsId | undefined,
    TemplateContentsId
  >;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;
}

export type MediaUsage = Selectable<MediaUsageTable>;

export type NewMediaUsage = Insertable<MediaUsageTable>;

export type MediaUsageUpdate = Updateable<MediaUsageTable>;

export const mediaUsageId = z.string() as unknown as z.Schema<MediaUsageId>;

export const mediaUsage = z.object({
  id: mediaUsageId,
  variantId: mediaVariantId,
  templateContentId: templateContentsId,
  createdAt: z.date(),
});

export const mediaUsageInitializer = z.object({
  id: mediaUsageId.optional(),
  variantId: mediaVariantId.optional(),
  templateContentId: templateContentsId.optional(),
  createdAt: z.date().optional(),
});

export const mediaUsageMutator = z.object({
  id: mediaUsageId.optional(),
  variantId: mediaVariantId.optional(),
  templateContentId: templateContentsId.optional(),
  createdAt: z.date().optional(),
});

// ── constraints for MediaUsage ─────────────────────────────────
export const MediaUsageConstraints = {
  media_usage_pkey: {
    name: "media_usage_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  media_usage_unique: {
    name: "media_usage_unique",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "variant_id", definition: "variant_id", predicate: null },
      { name: "template_content_id", definition: "template_content_id", predicate: null },
    ],
  },
  idx_media_usage_variant: {
    name: "idx_media_usage_variant",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "variant_id", definition: "variant_id", predicate: null }],
  },
  idx_media_usage_template_content: {
    name: "idx_media_usage_template_content",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "template_content_id", definition: "template_content_id", predicate: null }],
  },
  idx_media_usage_created_at: {
    name: "idx_media_usage_created_at",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "created_at", definition: "created_at", predicate: null }],
  },
} as const;
export type MediaUsageConstraintsType = typeof MediaUsageConstraints;
